//codex-- Keep the toolbar redesign scoped to pages in this folder.
import PageToolbar from './PageToolbar';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, NavLink } from 'react-router-dom';
import HeadingCp from '../components/HeadingCp';
import InspectionDetailsModal from '../components/InspectionDetailsModal';
import InspectionFormModal from '../components/InspectionFormModal';
import CalendarHeader from '../components/CalendarHeader';
import { formatTimeTo12HourUtil } from '../utils/formatTimeTo12HourUtil';
import DayViewTimeline from '../components/DayViewTimeline';
import { set } from 'date-fns';
import LegendCP from '../components/LegendCP';

const InspectionCalendarView = () => {
  const [inspections, setInspections] = useState([]);
  const [viewMode, setViewMode] = useState('week'); // 'day', 'week', 'month'
  const [loading, setLoading] = useState(false);

  // Confirm Delete Modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [formData, setFormData] = useState({
    maintenance_id: '',
    job_site_name: '',
    lead_tech_id: '',
    scheduled_due_date: '',
    schedule_date_to: '',
    actual_inspection_date: '',
    additional_tech_id: '',
    status: '',
    tentative: false,
    status_description: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [technicians, setTechnicians] = useState([]);

  // For Month/Week
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDates, setSelectedDates] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // For Day View
  const [selectedDay, setSelectedDay] = useState(new Date()); // default to "today"

  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [disabled, setDisabled] = useState(false);

  // ---------------- Date Helpers ----------------
  const getWeekStart = (date = new Date()) => {
      const dayOfWeek = date.getDay() || 7;
      const diff = date.getDate() - dayOfWeek + 1;
    return new Date(date.getFullYear(), date.getMonth(), diff);
  };

  const getWeekDates = (startOfWeek) => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(
        startOfWeek.getFullYear(),
        startOfWeek.getMonth(),
        startOfWeek.getDate() + i
      );
      dates.push(currentDay);
    }
    return dates;
  };

  const getMonthDates = (year, month) => {
    const date = new Date(year, month - 1, 1);
    const dates = [];
    while (date.getMonth() + 1 === month) {
      dates.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return dates;
  };

  // Helper: Parse 'YYYY-MM-DD' as a local date (not UTC)
  const parseLocalDate = (dateString) => {
    if (!dateString) return null;
    const [y, m, d] = dateString.split('-').map(Number);
    return new Date(y, m - 1, d, 0, 0, 0); // local time at midnight
  };


  // ---------------- useEffect: Update Calendar Range ----------------
  useEffect(() => {
    if (viewMode === 'week') {
      const today = new Date();
      const weekStart = getWeekStart(today);
      const weekDates = getWeekDates(weekStart);
      setSelectedDates(weekDates);
      setStartDate(weekDates[0]);
      setEndDate(weekDates[6]);
    } else if (viewMode === 'month') {
      const monthDates = getMonthDates(selectedYear, selectedMonth);
      setSelectedDates(monthDates);
      setStartDate(monthDates[0]);
      setEndDate(monthDates[monthDates.length - 1]);
    }
    // If Day View, we rely on selectedDay in a separate effect
    fetchTechnicians();
  }, [viewMode, selectedMonth, selectedYear, isModalOpen, isDetailModalOpen]);

  // For week/month, fetch once startDate/endDate are set
  useEffect(() => {
    if ((viewMode === 'week' || viewMode === 'month') && startDate && endDate) {
      fetchInspections(startDate, endDate);
    }
  }, [viewMode, startDate, endDate]);

  // For Day View, fetch data for just that day
  useEffect(() => {
    if (viewMode === 'day') {
      const dayStart = new Date(selectedDay);
      dayStart.setHours(0, 0, 0, 0);

      const dayEnd = new Date(selectedDay);
      dayEnd.setHours(23, 59, 59, 999);

      fetchInspections(dayStart, dayEnd);
    }
  }, [viewMode, selectedDay]);

   // ---------------- API Calls ----------------
  const fetchInspections = async (startD, endD) => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/contracts_crud.php`, {
        params: {
          getInspections: 1,
          month: 'all',
          startDate: startD.toISOString().split('T')[0],
          endDate: endD.toISOString().split('T')[0],
        },
      });
      const data = Array.isArray(response.data[0]) ? response.data[0] : [];
      setInspections(data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await axios.get(`${apiUrl}/contracts_crud.php`, {
        params: { employee_list: 'true' },
        headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      });
      const employeesData = Array.isArray(response.data) ? response.data : [];      
      if (employeesData.length === 0) {
        toast.error('No maintenance technicians found!', { position: 'top-center' });
      }
   /* ----------------------------------------------------------
    SORT technicians:
    1) by department  (A->Z)
    2) inside each department by last name  (A->Z)
    ---------------------------------------------------------- */
   const sorted = [...employeesData].sort((a, b) => {
   const depA = (a.department  || '').toLowerCase();
   const depB = (b.department  || '').toLowerCase();
   if (depA !== depB) return depA.localeCompare(depB);
   const lastA = (a.last_name   || '').toLowerCase();
   const lastB = (b.last_name   || '').toLowerCase();
    return lastA.localeCompare(lastB);
  });
  setTechnicians(sorted);                 
      // setTechnicians(employeesData);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleApiError = (error) => {
    if (error.response && error.response.status === 401) {
      toast.error('Session expired. Please login again.', {
        position: 'top-center',
      });
      navigate('/login');
    } else if (error.response && error.response.status === 403) {
      toast.error('Access denied.', {
        position: 'top-center',
      });
    } else {
      toast.error('An error occurred while fetching data.', {
        position: 'top-center',
      });
    }
  };

   // ---------------- Navigation / Handlers ----------------
  const handleMonthChange = (e) => {
    const [year, month] = e.target.value.split('-');
    setSelectedYear(parseInt(year, 10));
    setSelectedMonth(parseInt(month, 10));
  };

  const handlePreviousWeek = () => {
    const prevWeekStart = new Date(selectedDates[0]);
    prevWeekStart.setDate(prevWeekStart.getDate() - 7);
    const weekDates = getWeekDates(prevWeekStart);
    setSelectedDates(weekDates);
    setStartDate(weekDates[0]);
    setEndDate(weekDates[6]);
  };

  const handleNextWeek = () => {
    const nextWeekStart = new Date(selectedDates[0]);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);
    const weekDates = getWeekDates(nextWeekStart);
    setSelectedDates(weekDates);
    setStartDate(weekDates[0]);
    setEndDate(weekDates[6]);
  };

  const handleViewChange = (e) => {
    if (e.target.value === 'day') {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
    setViewMode(e.target.value);
  };

  const openModal = (isEdit = false) => {
    setIsModalOpen(true);
    setIsEditing(isEdit);
    // Clear form if new
    if (!isEdit) {
      setFormData({
        maintenance_id: '',
        job_site_name: '',
        lead_tech_id: '',
        scheduled_due_date: '',
        schedule_date_to: '',
        actual_inspection_date: '',
        status: '',
        tentative: false,
        status_description: ''
      });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

   // ---------------- Form Submit ----------------
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const from = formData.scheduled_due_date;   // "YYYY-MM-DD"
    const to   = formData.schedule_date_to;     // "YYYY-MM-DD"
    const next = formData.next_due_date;        // "YYYY-MM-DD"

    // Only validate if values exist
    if (from && to) {
      if (from > to) {
        toast.error('Scheduled "from" date cannot be later than "to" date.', { position: 'top-center' });
        setLoading(false);
        return;
      }
    }
    if(formData.scheduled_due_date <= formData.scheduled_date_to)
    {
      toast.error('Scheduled from date must be later than scheduled to date.', {
        position: 'top-center',
      });
      setLoading(false);
      return;
    }
    if(parseInt(formData.lead_tech_id) === parseInt(formData.additional_tech_id)) { 
      toast.error('You cannot use the same technician as lead and additional.', {
        position: 'top-center',
      });
      setLoading(false);
      return;
    }    

    // inside onSubmit before sending:
    if (formData.tentative && !String(formData.status_description || '').trim()) {
      toast.error('Please provide a Status Description for tentative inspections.', { position: 'top-center' });
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${apiUrl}/contracts_crud.php`, {
        data: formData,
        status: 'recordInspection',
      });
      if (response.data.success) {
        toast.success('Inspection saved successfully!');
        // 1) Reset the form data, including job site fields
        setFormData({
          maintenance_id: '',
          job_site_name: '',
          next_due_date: '',
          scheduled_due_date: '',
          schedule_date_to: '',
          actual_inspection_date: '',
          status: '',
          lead_tech_id: '',
          additional_tech_id: '',
          schedule_time_in: '',
          schedule_time_out: '',
          inspection_notes: '',
          tentative: false,
          status_description: ''
          // whatever else you need to clear…
        });
        // 2) Close the modal and reload your data
        closeModal();
        reFetchCurrentView();
      } else {
        toast.error(response.data.error || 'Failed to save inspection.');
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Delete with Confirm ----------------
  // 1. User clicks Delete in the form => show confirm modal
  const handleDeleteRequest = () => {
    setDeleteId(formData?.id);
    setShowConfirmModal(true);
    setIsModalOpen(false);
  };

  // 2. User confirms => call delete
  const confirmDelete = async () => {
    setShowConfirmModal(false);
    try {
      const response = await axios.post(`${apiUrl}/contracts_crud.php`, {
        data: { id: deleteId },
        status: 'deleteInspection',
      });
      if (response.data.success) {
        toast.success('Inspection successfully deleted!');
        closeModal();
        reFetchCurrentView();
      } else {
        toast.error(response.data.error || 'Failed to delete inspection.');
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  // 3. User cancels => just hide the confirm modal
  const cancelDelete = () => {
    setShowConfirmModal(false);
  };

  // Utility to re-fetch data based on current view mode
  const reFetchCurrentView = () => {
    if (viewMode === 'day') {
      const dayStart = new Date(selectedDay);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(selectedDay);
      dayEnd.setHours(23, 59, 59, 999);
      fetchInspections(dayStart, dayEnd);
    } else if (viewMode === 'week' || viewMode === 'month') {
      if (startDate && endDate) {
        fetchInspections(startDate, endDate);
      }
    }
  };

  // ---------------- Click Handlers ----------------
  const handleEventClick = (inspection) => {
    setSelectedInspection(inspection);
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
  };

  const handleEditInspection = () => {
    const editableInspection = {
      ...selectedInspection,
      id: selectedInspection.inspection_id, // rename for the form
      tentative: selectedInspection.status === 'tentative' ? true : false
    };
    setFormData(editableInspection);
    setIsDetailModalOpen(false);
    setIsModalOpen(true);
    setIsEditing(true);
  };

  // For week/month: cell click => new inspection
  const handleCellClick = (date, technician) => {
    openModal(false);
    setFormData((prevData) => ({
      ...prevData,
      scheduled_due_date: date.toISOString().split('T')[0],
      schedule_date_to: date.toISOString().split('T')[0],
      lead_tech_id: technician?.id || '',
    }));
  };

  // For day view, user picks a day from a date input
  const handleDayChange = (e) => {
    const [year, month, day] = e.target.value.split('-').map(Number);
    const newDay = new Date(year, month - 1, day, 12, 0, 0); // local date at noon
    setSelectedDay(newDay);
  };

  // ---------------- Render ----------------
  return (
    <div className="mx-auto w-[90vw] min-w-[72rem] py-10 sm:px-6 lg:px-8">
      <HeadingCp label="Inspection Calendar View" />

      {/* Top Control Row */}
      <>{/*codex-- Match the updated toolbar: grouped actions, labeled controls, and lighter original colors. */}
        <PageToolbar title="Inspection calendar tools" actions={<>
          <NavLink to="/full_scheduler_viewer" className="hidden sm:inline-flex">
            <button type="button" className="w-full sm:w-auto px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-400 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500">Full Scheduler</button>
          </NavLink>
          <button type="button" onClick={() => openModal(false)} className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-400 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500">New Inspection +</button>
          <button type="button" onClick={() => navigate(`/maintenance`)} className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-400 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500">Contracts</button>
          <button onClick={() => navigate(`/maintenance_inspections`)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black-500">Inspections Dashboard</button>
        </>}>
          <div className="page-toolbar-row">
            <label htmlFor="view-mode">View:</label>
            <select id="view-mode" className="rounded-md border border-gray-300 py-1 px-2" value={viewMode} onChange={handleViewChange}>
              <option value="day">Day View</option>
              <option value="week">Week View</option>
              <option value="month">Month View</option>
            </select>
          </div>
          {viewMode === 'day' && <div className="page-toolbar-row">
            <label htmlFor="day-selector">Select Day:</label>
            <input type="date" id="day-selector" value={selectedDay.toISOString().split('T')[0]} onChange={handleDayChange} className="rounded-md border border-gray-300 py-1 px-2" />
          </div>}
          {viewMode === 'week' && <CalendarHeader viewMode={viewMode} selectedWeek={selectedDates} handlePreviousWeek={handlePreviousWeek} handleNextWeek={handleNextWeek} />}
        </PageToolbar>
      </>
      <div className="py-2">
        <span className="text-xs text-gray-500 pr-2">Status Week View</span>
        <LegendCP color="fill-orange-300" label="Tentative" />
        <LegendCP color="fill-blue-300" label="Pending" />
        <LegendCP color="fill-green-400" label="Completed" />
      </div>
      {/* Conditional Render of Different Views */}
      {/* Day View */}
      {viewMode === 'day' && (() => {
        const dayISO  = selectedDay.toISOString().split('T')[0];
        const dayInspections = inspections.filter(insp => {
          const fromDate = parseLocalDate(insp.scheduled_due_date);
          const toDate = parseLocalDate(insp.schedule_date_to || insp.scheduled_due_date);
          if (!fromDate || !toDate) return false;
          fromDate.setHours(0, 0, 0, 0);
          toDate.setHours(23, 59, 59, 999);
          return selectedDay >= fromDate && selectedDay <= toDate;
        });

        const techInspectionsMap = {};
        technicians.forEach(tech => {
          const techId = String(tech.id);
          techInspectionsMap[techId] = dayInspections.filter(insp =>
            String(insp.lead_tech_id) === techId ||
            (insp.additional_tech_id && String(insp.additional_tech_id) === techId)
          );
        });        

        return (
        <DayViewTimeline
          selectedDay={selectedDay}
          technicians={technicians}            // <-- the full list (array)
          techInspectionsMap={techInspectionsMap} // <-- your map (object: techId -> inspections[])
          onEventClick={(insp) => {
            setSelectedInspection(insp);
            setIsDetailModalOpen(true);
          }}
          onEmptyClick={({ hour, minute, techId }) => {
            openModal(false);
            const hh = String(hour).padStart(2,'0');
            const mm = String(minute).padStart(2,'0');
            const day = selectedDay.toISOString().split('T')[0];

            setFormData(prev => ({
              ...prev,
              scheduled_due_date : day,
              schedule_date_to   : day,
              schedule_time_in   : `${hh}:${mm}:00`,
              schedule_time_out  : `${hh === '23' ? hh : String(hour+1).padStart(2,'0')}:00:00`,
              lead_tech_id       : techId
            }));
          }}
        />
        );
      })()}


      {(viewMode === 'week' || viewMode === 'month') && (
        <WeekOrMonthView
          viewMode={viewMode}
          selectedDates={selectedDates}
          technicians={technicians}
          inspections={inspections}
          handleEventClick={handleEventClick}
          handleCellClick={handleCellClick}
        />
      )}

      {/* ---------------- Inspection Form Modal ---------------- */}
      <InspectionFormModal
        isOpen={isModalOpen}
        onClose={closeModal}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleFormSubmit}
        technicians={technicians}
        handleDelete={handleDeleteRequest} 
        isEditing={isEditing}
        disabled={disabled}
        loading={loading}
      />

      {/* ---------------- Confirm Deletion Modal ---------------- */}
      {showConfirmModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen text-center">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            {/* Modal panel */}
            <div
              className="inline-block bg-white rounded-lg text-left 
              overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Confirm Deletion
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to delete this inspection? This action cannot be
                      undone.
                    </p>
                  </div>
                </div>
              </div>
              {/* Modal actions */}
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse">
                <button
                  onClick={confirmDelete}
                  className="w-full inline-flex justify-center rounded-md border border-transparent 
                  shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Delete
                </button>
                <button
                  onClick={cancelDelete}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 
                  shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Inspection Details Modal ---------------- */}
      <InspectionDetailsModal
        isOpen={isDetailModalOpen}
        onClose={closeDetailModal}
        inspection={selectedInspection}
        onEdit={handleEditInspection}
      />
    </div>
  );
};

// -------------------- WeekOrMonthView Component --------------------
const WeekOrMonthView = ({
  viewMode,
  selectedDates,
  technicians,
  inspections,
  handleEventClick,
  handleCellClick,
}) => {
  // parseLocalDate helper
  const parseLocalDate = (dateString) => {
    // if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day, 0, 0, 0);
  };

  // For each date/technician, find any inspection
  const getScheduledInspections = (date, technicianId) => {
    const currentDate = new Date(date);
    currentDate.setHours(0, 0, 0, 0);

    return inspections.filter((insp) => {
      const fromDate = parseLocalDate(insp.scheduled_due_date);
      const toDate = parseLocalDate(insp.schedule_date_to || insp.scheduled_due_date);

      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);

      const leadId = String(insp.lead_tech_id);
      const addlId = String(insp.additional_tech_id || '');

      const techMatch = leadId === technicianId || addlId === technicianId;
      const dateInRange = currentDate >= fromDate && currentDate <= toDate;

      return techMatch && dateInRange;
    });
  };

  return (
    <div className="overflow-auto">
      <div
        className="grid"
        style={{ gridTemplateColumns: `200px repeat(${selectedDates.length}, 1fr)` }}
      >
        {/* blank top-left corner */}
        <div className="bg-gray-100 sticky top-0 z-10" />

        {/* Date headers */}
        {selectedDates.map((date, idx) => (
          <div key={idx} className="bg-gray-100 text-center p-2 sticky top-0 z-10 border">
            {date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })}
          </div>
        ))}

        {/* Technician rows */}
        {technicians.map((tech, rowIndex) => (
          <React.Fragment key={tech.id || rowIndex}>
            <div className="border p-2 bg-white sticky left-0 z-10">
              <div className="font-bold text-black">
                {tech.first_name} {tech.last_name}
                {tech.title && (
                <div className={`text-xs ${tech.title.toLowerCase().includes('helper') ? 'text-yellow-500' 
                                                                                        : tech.o_f_type === 'Field' 
                                                                                        ? 'text-blue-600' 
                                                                                        : 'text-gray-600'}`}>
                                                                                          {tech.title}
                </div>
                )}                 
                <div className="text-xs text-gray-700">{tech.department}</div>
              </div>
            </div>

            {selectedDates.map((date, colIndex) => {
              const techId = String(tech.id);
              const scheduledInspections = getScheduledInspections(date, techId);
              const isScheduled = scheduledInspections.length > 0;
              const isCompleted = isScheduled && scheduledInspections[0].status === 'Completed';              

              return (
                <div
                  key={`${tech.id || rowIndex}-${colIndex}`}
                  // className={`border h-16 cursor-pointer hover:bg-slate-100 ${
                  //   isScheduled ? 'bg-blue-200' : 'bg-white'
                  // }`}
                   className={`border h-16 cursor-pointer hover:bg-slate-100 ${
                     isScheduled
                       ? isCompleted
                         ? 'bg-green-300'        /* finished inspections  */
                         : scheduledInspections[0].status === 'tentative'
                          ? 'bg-orange-300'       /* tentative inspections */
                          : 'bg-blue-200'         /* scheduled / upcoming */
                            : 'bg-white'              /* empty cell            */
                   }`}                  
                  onClick={() =>
                    isScheduled
                      ? handleEventClick(scheduledInspections[0])
                      : handleCellClick(date, tech)
                  }
                >
                  {isScheduled && (
                    <>
                      <p className="font-bold text-xs text-center">
                        {scheduledInspections[0].job_site_address || 'Unnamed'}
                      </p>
                      <p className="text-green-600 text-xs text-center">
                        {scheduledInspections[0].customer_name || 'customer missing'}
                      </p>
                      <p className="text-gray-600 text-xs text-left pl-1">
                        {scheduledInspections[0].schedule_time_in
                          ? formatTimeTo12HourUtil(scheduledInspections[0].schedule_time_in)
                          : 'no time'}
                        {scheduledInspections[0].schedule_time_out
                          ? ' - ' + formatTimeTo12HourUtil(scheduledInspections[0].schedule_time_out)
                          : ' - no time'}
                      </p>
                    </>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default InspectionCalendarView;
